import { onBeforeUnmount, watch, type Ref } from "vue";

/**
 * Détection de balayage horizontal sur un élément, via les Pointer Events.
 *
 * Utilisée par la visionneuse pour naviguer au doigt entre les souvenirs,
 * en plus des boutons et du clavier.
 */

interface SwipeHandlers {
  onLeft?: () => void;
  onRight?: () => void;
  /** Distance minimale en pixels avant de considérer le geste comme un balayage. Défaut 50. */
  threshold?: number;
}

const DEFAULT_THRESHOLD = 50;

export function useSwipe(target: Ref<HTMLElement | null>, handlers: SwipeHandlers): void {
  let startX = 0;
  let startY = 0;
  let tracking = false;

  const onPointerDown = (event: PointerEvent): void => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    tracking = true;
    startX = event.clientX;
    startY = event.clientY;
  };

  const onPointerUp = (event: PointerEvent): void => {
    if (!tracking) return;
    tracking = false;

    const deltaX = event.clientX - startX;
    const deltaY = event.clientY - startY;
    const threshold = handlers.threshold ?? DEFAULT_THRESHOLD;

    // Un geste surtout vertical (défilement de page) n'est jamais un balayage.
    if (Math.abs(deltaY) > Math.abs(deltaX)) return;
    if (Math.abs(deltaX) < threshold) return;

    if (deltaX < 0) {
      handlers.onLeft?.();
    } else {
      handlers.onRight?.();
    }
  };

  const onPointerCancel = (): void => {
    tracking = false;
  };

  const attach = (element: HTMLElement): void => {
    element.addEventListener("pointerdown", onPointerDown);
    element.addEventListener("pointerup", onPointerUp);
    element.addEventListener("pointercancel", onPointerCancel);
  };

  const detach = (element: HTMLElement): void => {
    element.removeEventListener("pointerdown", onPointerDown);
    element.removeEventListener("pointerup", onPointerUp);
    element.removeEventListener("pointercancel", onPointerCancel);
  };

  watch(
    target,
    (element, previousElement) => {
      if (previousElement) detach(previousElement);
      if (element) attach(element);
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    if (target.value) detach(target.value);
  });
}
